/*
 * Types for the Agent Configuration tab.
 *
 * The conductor contracts come from the generated OpenAPI schemas, served by
 * `/sysconfig/api/v1/organizations/<id>/conductor_configuration`; the expert contracts come from
 * `/sysconfig/api/v1/organizations/<id>/ai_experts`. `ScopeRow` is client-only, derived from the
 * sysconfig organization context rather than from an endpoint.
 */

import type {
  AiExpertInfo,
  AiExpertScopeConfigurationInfo,
  AiExpertScopeConfigurationInfoConfiguredField,
  AiExpertScopeConfigurationInfoStructuredOutputComponent,
  ConductorConfigurationModel,
  ConductorScopeConfigurationModel,
  ConductorScopePatchJson,
  SetAiExpertScopeRequest as GeneratedSetAiExpertScopeRequest,
} from '~/server_shared/openapi/data-contracts';

export type { ConductorConfigurationModel, ConductorScopeConfigurationModel, ConductorScopePatchJson };
export type { AiExpertInfo, AiExpertScopeConfigurationInfo };
export type { ScopeRow } from '../scope';

/**
 * Body of `PUT .../ai_experts/scopes/<expert_key>`.
 *
 * The generated contract types every field optional, because each one carries a server-side
 * default. `Required` is the whole point of re-declaring it here: the write is a whole-row
 * upsert, so a field the body omits is *reset to its default* rather than left unchanged, and
 * making every field mandatory is what stops a screen that owns part of the row from silently
 * clearing what another screen set. Both this tab and the QA Configuration tab write the same
 * `qa_expert` row, so they share this one type rather than each spelling it out.
 */
export type SetAiExpertScopeRequest = Required<
  Omit<GeneratedSetAiExpertScopeRequest, 'configured_fields' | 'render_component'>
> & {
  // The generator emits a separate nominal enum/interface per schema occurrence, so the request
  // and response copies of these two are distinct types with identical members. The screens hold
  // the response-side values and send them straight back, so the round-trip is spelled in those.
  configured_fields: AiExpertScopeConfigurationInfoConfiguredField[];
  render_component: AiExpertScopeConfigurationInfoStructuredOutputComponent;
};

/**
 * What the expert dialog hands back on Save: the whole row minus the scope, which the editor
 * addresses itself -- the same division `handlePatch` uses for the conductor's sections.
 */
export type AiExpertScopeValues = Omit<SetAiExpertScopeRequest, 'sub_organization_id' | 'lob'>;

/**
 * Whether this scope runs the expert at all -- under the conductor, standalone, or both.
 *
 * Mirrors the `is_enabled` the server derives from the same two columns. Both false and an absent
 * row mean the same thing, which is why this takes the row as optional rather than making each
 * caller spell out the absence case.
 */
export const isExpertScopeEnabled = (row: AiExpertScopeConfigurationInfo | undefined): boolean =>
  Boolean(row && (row.conductor_enabled || row.standalone_enabled));

/**
 * The experts this screen configures.
 *
 * `is_enabled` is the catalogue's cross-organization switch -- an expert turned off there is
 * offered to no organization at all -- and demo experts are authored and configured from the
 * admin catalogue screen instead, so neither belongs in a scope's list. Declared once because
 * three readers need the same set: the section, its header count, and the scope table's column.
 */
export const isScopeConfigurableExpert = (expert: AiExpertInfo): boolean => expert.is_enabled && !expert.is_demo;

/**
 * How an expert runs for one scope, as the screen puts it: one choice, never two.
 *
 * Storage keeps the two independent booleans the server writes, because running under the
 * conductor *and* standalone is a combination the product expects to allow later. The screen
 * does not offer it yet, so this type is the client-side narrowing of that pair, and
 * `runModeFlags` is the only place the mapping back is written.
 */
export const EXPERT_RUN_MODES = {
  OFF: 'off',
  CONDUCTOR: 'conductor',
  STANDALONE: 'standalone',
} as const;

export type ExpertRunMode = (typeof EXPERT_RUN_MODES)[keyof typeof EXPERT_RUN_MODES];

/**
 * The stored row read as one mode.
 *
 * A row with *both* booleans set cannot be produced by this screen, and neither the migration
 * nor the QA tab writes one; only a hand-written API call can. It reads as `CONDUCTOR` here, and
 * saving the dialog then normalises it to exactly that -- which is why the table renders the
 * stored pair rather than this value, so a both-on row is visible for what it is.
 */
export const expertRunMode = (row: AiExpertScopeConfigurationInfo | undefined): ExpertRunMode => {
  if (row?.conductor_enabled) return EXPERT_RUN_MODES.CONDUCTOR;
  if (row?.standalone_enabled) return EXPERT_RUN_MODES.STANDALONE;
  return EXPERT_RUN_MODES.OFF;
};

/** The column pair one mode writes. The single place the narrowing above is inverted. */
export const runModeFlags = (mode: ExpertRunMode): { conductor_enabled: boolean; standalone_enabled: boolean } => ({
  conductor_enabled: mode === EXPERT_RUN_MODES.CONDUCTOR,
  standalone_enabled: mode === EXPERT_RUN_MODES.STANDALONE,
});

/** Local draft held by the expert dialog and committed on Save. */
export interface ExpertDialogFormValues {
  run_mode: ExpertRunMode;
  /** Blank while unset: the number input clears to an empty string, and blank means no threshold. */
  standalone_confidence_threshold: number | '';
  instructions: string;
}

/** One stage's authored content, keyed in `ConductorScopeConfigurationModel.stages` by stage key. */
export type ConductorStageContent = NonNullable<ConductorScopeConfigurationModel['stages']>[string];

/**
 * A claim stage as the stages list renders it: the stage itself, from the organization's
 * claim-stage configuration, joined to whatever the conductor has authored against its key.
 *
 * `content` is `undefined` for an enabled stage nobody has configured yet — which is a real
 * state, not an error, and the one the "Not configured" chip exists for. A stage whose content
 * has only one of goal and instructions is also "Not configured": the chip needs both (see
 * Conductor/isStageConfigured.ts).
 */
export interface ConductorStageRow {
  key: string;
  label: string;
  content?: ConductorStageContent;
}

/** Local draft held by the stage dialog and committed on Save. */
export interface StageDialogFormValues {
  goal: string;
  instructions: string;
}

/** Local draft held by the general-instructions dialog and committed on Save. */
export interface GeneralInstructionsFormValues {
  general_instructions: string;
}
